import { Component } from "preact";
import actions from "../../actions";
import { connectWithTranslations } from "../../../common/helpers/translationsHelpers";
import { isPast } from "../../../common/helpers/dateHelpers";
import IconInfoCircle from "../../../common/components/icons/IconInfoCircle";
import IconBell from "../../../common/components/icons/IconBell";
import IconBellSolid from "../../../common/components/icons/IconBellSolid";
import IconChartBar from "../../../common/components/icons/IconChartBar";
import IconCalendarPlus from "../../../common/components/icons/IconCalendarPlus";
import IconEllipsis from "../../../common/components/icons/IconEllipsis";
import IconTimes from "../../../common/components/icons/IconTimes";
import IconFileSolid from "../../../common/components/icons/IconFileSolid";
import EventTime from "./EventTime";
import { COUNTRY_FILTER_OPTIONS, GA4EVENT, GTM_ID } from "../../constants";
import { urlFactory, downloadCalendar, formatTemplate } from "../../helpers";
import renderNumerical from "../../helpers/render";
import { getDeviation } from "../../helpers/deviation";
import { CALENDAR_VIEW } from "../../../calendar-common/constants";
import { enabledCalendarNotifications } from "../../../calendar-common/helpers/calendar";
import IconLocked from "../../../common/components/icons/IconLocked";
import SpeechScore from "./SpeechScore";
import SpeechAverageScore from "./SpeechAverageScore";
import eventTimeCheck from "../../helpers/eventTimeCheck";
import { getSpeechScore } from "../../helpers/getSpeechScore";

class EventRowDataDesktop extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isPastEvent: isPast(props.event.date, props.settings.timezoneOffset),
        };
    }

    renderTradeNow(config, eventdate) {
        try {
            return (
                <span>
                    &nbsp;
                    <span
                        dangerouslySetInnerHTML={{
                            __html: config.tradeNowFnc(eventdate),
                        }}
                    ></span>
                </span>
            );
        } catch (ex) {
            console.log("tradNowFunc", ex);
        }
    }

    getOneColumnLabelText(isSpeech, isReport, isInProgress) {
        return isSpeech
            ? isInProgress
                ? "InProgress"
                : "RowSpeech"
            : isReport
            ? "RowReport"
            : "RowAllDay";
    }

    renderOneColumnData(isSpeech, labelText, translate, speechScore, isInProgress) {
        if (speechScore?.isLocked) {
            return (
                <span className="fxs_c_label fxs_c_label_info_background fxs_c_label_locked">
                    <span class="fxs_c_label_locked_icon">
                        <IconLocked />
                    </span>
                    <span class="fxs_c_label_locked_text">
                        {translate("TrackerLocked")}
                    </span>
                </span>
            );
        }

        if (!isSpeech || !speechScore) {
            return (
                <span
                    className={`fxs_c_label ${
                        isInProgress
                            ? "fxs_c_label_info_base"
                            : "fxs_c_label_info_background"
                    }`}
                >
                    <span>{translate(labelText)}</span>
                </span>
            );
        }

        if (speechScore?.score == null && speechScore?.averageScore != null) {
            return (
                <div className="fxs_c_speech_score_wrapper">
                    <span
                        className={`fxs_c_label ${
                            isInProgress
                                ? "fxs_c_label_info_base"
                                : "fxs_c_label_info_background"
                        }`}
                    >
                        <span>{translate(labelText)}</span>
                    </span>
                    <span className="fxs_c_pipe">|</span>
                    <span>{translate("Average").concat(": ")}</span>
                    <SpeechAverageScore speechScore={speechScore} />
                </div>
            );
        }

        return this.speechScoreBuilder({
            translate,
            speechScore,
        });
    }

    speechScoreBuilder = ({ translate, speechScore }) => {
        return (
            <div className="fxs_c_speech_score_wrapper">
                <SpeechScore speechScore={speechScore} forceUpperCase={true} />
                {speechScore?.averageScore != null && (
                    <>
                        <span className="fxs_c_pipe">|</span>
                        <span>{translate("Average").concat(": ")}</span>
                        <SpeechAverageScore speechScore={speechScore} />
                    </>
                )}
            </div>
        );
    };

    render(
        {
            event,
            translate,
            showDetailsClick,
            showEventDateMenu,
            canBeSubscribed,
            isSubscribed,
            subscribeToEvent,
            unsubscribeToEvent,
            eventDateMenuShown,
            eventDateDetailShown,
            renderName,
            config,
            settings: { timezoneOffset, view },
            eventTimeReached,
        },
        { isPastEvent }
    ) {
        const {
            id,
            date,
            countryCode,
            currencyCode,
            speech,
            isSpeech,
            isReport,
            isAllDay,
            isTentative,
            isPreliminary,
            actual,
            volatility,
            ratioDeviation,
            consensus,
            previous,
            potency,
            unit,
            revised,
            isBetterThanExpected,
            eventId,
            isScoreTrackable,
        } = event;

        let speechScore = null;

        const hasScore = speech?.score ?? false;
        const hasAverageScore = speech?.averageScore ?? false;

        const isSpeechScoreEnabled = config.enableSpeechScore;

        const isInProgress =
            !hasScore && isScoreTrackable && eventTimeReached && isSpeechScoreEnabled;
        const countryName = getCountryName(countryCode);
        const prefix = unit !== "%" ? unit : null;
        const percentage = unit === "%" ? "%" : null;

        const isOneColumn = isSpeech || isReport || isAllDay;
        const showEventTime = !isTentative || actual !== null;
        const showDashIcon = !isOneColumn && config.dashboardUrl;
        const eventDateDetailIsShown = eventDateDetailShown === id;

        if (isSpeech && isSpeechScoreEnabled && (hasScore || hasAverageScore)) {
            speechScore = getSpeechScore(event);
            if (speechScore) speechScore.isLocked = config.isLocked();
        }

        return (
            <tr
                data-event-date-id={id}
                class={`fxs_c_row ${isPastEvent ? "fxs_c_now" : ""} ${
                    eventDateDetailIsShown ? "fxs_c_event_detail_shown" : ""
                }`}
                onClick={showDetailsClick}
                data-gtmid={config.tracking && GTM_ID.OPEN_OVERVIEW(id)}
                data-ga4event-clk-name={
                    config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                }
                data-ga4event-element={config.tracking && GA4EVENT.ELEMENTS.OPEN_OVERVIEW}
                data-ga4event-eventdateid={id}
            >
                <td class="fxs_c_item fxs_c_time">
                    {showEventTime ? (
                        <EventTime
                            date={date}
                            onEventHappened={() => this.setState({ isPastEvent: true })}
                            hasActual={actual !== null}
                        />
                    ) : (
                        translate("RowNotAvailable")
                    )}
                </td>

                <td class="fxs_c_item fxs_c_flag">
                    <span
                        title={translate(countryName)}
                        class={`fxs_flag ${
                            countryCode ? "fxs_" + countryCode.toLowerCase() : ""
                        }`}
                    ></span>
                </td>

                <td class="fxs_c_item fxs_c_currency">
                    <span>{currencyCode}</span>
                </td>

                <td class="fxs_c_item fxs_c_name">
                    {renderName(event)}
                    {isPreliminary && (
                        <span class="fxs_c_preliminary">
                            {" "}
                            {translate("RowPreliminaryAbbreviation")}{" "}
                        </span>
                    )}
                    {config.tradeNowFnc !== null && this.renderTradeNow(config, event)}
                </td>

                <td class="fxs_c_item fxs_c_impact">
                    <span
                        class={`fxs_c_impact-icon ${
                            volatility
                                ? "fxs_c_impact-" + volatility.toLowerCase()
                                : "fxs_c_impact-none"
                        }`}
                    ></span>
                </td>

                {isOneColumn && (
                    <td
                        colspan={view == CALENDAR_VIEW.Configure_View_Basic ? "3" : "4"}
                        class="fxs_c_item fxs_c_type"
                    >
                        {this.renderOneColumnData(
                            isSpeech,
                            this.getOneColumnLabelText(isSpeech, isReport, isInProgress),
                            translate,
                            speechScore,
                            isInProgress
                        )}
                    </td>
                )}

                {!isOneColumn && (
                    <td class="fxs_c_item fxs_c_actual">
                        {
                            <strong
                                class={
                                    typeof isBetterThanExpected === "boolean"
                                        ? isBetterThanExpected
                                            ? "fxs_c_positive"
                                            : "fxs_c_negative"
                                        : ""
                                }
                            >
                                {renderNumerical(
                                    actual,
                                    prefix,
                                    percentage,
                                    potency,
                                    translate
                                )}
                            </strong>
                        }
                    </td>
                )}
                {!isOneColumn && view != CALENDAR_VIEW.Configure_View_Basic && (
                    <td class="fxs_c_item fxs_c_deviation">
                        {ratioDeviation !== null && ratioDeviation !== undefined
                            ? ratioDeviation.toFixed(2)
                            : getDeviation(actual, consensus)}
                    </td>
                )}

                {!isOneColumn && (
                    <td class="fxs_c_item fxs_c_consensus">
                        {renderNumerical(
                            consensus,
                            prefix,
                            percentage,
                            potency,
                            translate
                        )}
                    </td>
                )}

                {!isOneColumn && (
                    <td class="fxs_c_item fxs_c_previous">
                        {typeof revised !== "undefined" && revised !== null && (
                            <span class="fxs_c_has-tooltip">
                                <span>
                                    {renderNumerical(
                                        revised,
                                        prefix,
                                        percentage,
                                        potency,
                                        translate
                                    )}
                                </span>
                                <span class="fxs_c_tooltip">
                                    <IconInfoCircle />
                                    <span class="fxs_c_content">
                                        <span class="fxs_c_textnode">
                                            {translate("PreviousTooltip", previous)}
                                        </span>
                                    </span>
                                </span>
                            </span>
                        )}
                        {typeof revised === "undefined" ||
                            (revised === null &&
                                renderNumerical(
                                    previous,
                                    prefix,
                                    percentage,
                                    potency,
                                    translate
                                ))}
                    </td>
                )}
                <td
                    class={`fxs_c_item fxs_c_notify ${
                        isSubscribed ? "fxs_c_notify_active" : ""
                    }`}
                >
                    {canBeSubscribed && enabledCalendarNotifications(config) && (
                        <button
                            type="button"
                            className="fxs_btn fxs_btn_icon"
                            data-gtmid={
                                config.tracking && isSubscribed
                                    ? GTM_ID.NOTIFY_ME_UNSUBSCRIPTION(id)
                                    : GTM_ID.NOTIFY_ME_SUBSCRIPTION(id)
                            }
                            data-ga4event-clk-name={
                                config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                            }
                            data-ga4event-element={
                                config.tracking && isSubscribed
                                    ? GA4EVENT.ELEMENTS.NOTIFY_ME_UNSUBSCRIPTION
                                    : GA4EVENT.ELEMENTS.NOTIFY_ME_SUBSCRIPTION
                            }
                            data-ga4event-eventdateid={id}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isSubscribed) unsubscribeToEvent(eventId);
                                else {
                                    subscribeToEvent(eventId);
                                }
                            }}
                        >
                            {isSubscribed && enabledCalendarNotifications(config) ? (
                                <IconBellSolid />
                            ) : (
                                <IconBell />
                            )}
                        </button>
                    )}
                </td>

                <td
                    class={
                        eventDateMenuShown === id
                            ? "fxs_c_item fxs_c_dashboard fxs_active"
                            : eventDateDetailShown === id
                            ? "fxs_c_item fxs_c_dashboard fxs_c_detailActive"
                            : "fxs_c_item fxs_c_dashboard"
                    }
                    data-gtmid={
                        config.tracking &&
                        (eventDateDetailShown === id
                            ? GTM_ID.CLOSE_OVERVIEW(id)
                            : GTM_ID.OPEN_GRAPH(id))
                    }
                    data-ga4event-clk-name={
                        config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                    }
                    data-ga4event-element={
                        eventDateDetailShown === id
                            ? GA4EVENT.ELEMENTS.CLOSE_OVERVIEW
                            : GA4EVENT.ELEMENTS.OPEN_GRAPH
                    }
                    data-ga4event-eventdateid={id}
                    onClick={(e) => {
                        if (eventDateDetailShown !== id) {
                            e.stopPropagation();
                            showEventDateMenu(id);
                        }
                    }}
                >
                    {eventDateDetailShown === id ? (
                        <IconTimes />
                    ) : (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                showEventDateMenu(id);
                            }}
                        >
                            <IconEllipsis />
                        </span>
                    )}
                    {eventDateMenuShown === id && (
                        <div class="fxs_c_options" onClick={(e) => e.stopPropagation()}>
                            <p
                                onClick={(e) => {
                                    if (eventDateDetailShown !== id) showDetailsClick();
                                }}
                                data-gtmid={config.tracking && GTM_ID.OPEN_OVERVIEW(id)}
                                data-ga4event-clk-name={
                                    config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                                }
                                data-ga4event-element={
                                    config.tracking && GA4EVENT.ELEMENTS.OPEN_OVERVIEW
                                }
                                data-ga4event-eventdateid={id}
                            >
                                <IconFileSolid /> {translate("Menu_Overview")}
                            </p>
                            {showDashIcon && (
                                <p>
                                    <a
                                        href={formatTemplate(config.dashboardUrl, {
                                            eventId,
                                            timezoneOffset,
                                        })}
                                        onClick={() => showEventDateMenu(id)}
                                        target="_blank"
                                        title={translate("Dashboard")}
                                        data-gtmid={
                                            config.tracking && GTM_ID.OPEN_DASHBOARD(id)
                                        }
                                        data-ga4event-clk-name={
                                            config.tracking &&
                                            GA4EVENT.NAMES.FEATURES_CALENDAR
                                        }
                                        data-ga4event-element={
                                            config.tracking &&
                                            GA4EVENT.ELEMENTS.OPEN_DASHBOARD
                                        }
                                        data-ga4event-eventdateid={id}
                                    >
                                        <IconChartBar /> {translate("Dashboard")}
                                    </a>
                                </p>
                            )}
                            <p>
                                <a
                                    data-gtmid={config.tracking && GTM_ID.ADD_EVENT(id)}
                                    data-ga4event-clk-name={
                                        config.tracking &&
                                        GA4EVENT.NAMES.FEATURES_CALENDAR
                                    }
                                    data-ga4event-element={
                                        config.tracking && GA4EVENT.ELEMENTS.ADD_EVENT
                                    }
                                    data-ga4event-eventdateid={id}
                                    onClick={() => {
                                        showEventDateMenu(id);
                                        downloadCalendar(
                                            urlFactory.getEventDateICalendar(config, id),
                                            "text/calendar"
                                        );
                                    }}
                                    class="fxs_c_mutedLink"
                                >
                                    <IconCalendarPlus />
                                    <span> </span>
                                    <span class="fxs_c_entry_metaInfo">
                                        {translate("DetailAddEvent")}
                                    </span>
                                </a>
                            </p>
                        </div>
                    )}
                </td>
            </tr>
        );
    }
}

const getCountryName = (countryCode) => {
    var countries = Object.entries(COUNTRY_FILTER_OPTIONS).filter(
        (k) => COUNTRY_FILTER_OPTIONS.key !== null
    );
    var countryName = countries.find(([k, v]) => countryCode.includes(v));
    return countryName ? countryName[0] : "";
};

export default connectWithTranslations(
    "config,settings,eventDateMenuShown,eventDateDetailShown",
    actions
)(eventTimeCheck(EventRowDataDesktop));
